import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, filter } from 'rxjs';

import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { environment } from '../../../../../apps/gs-2.0/src/environments/environment';
import { IdleActivityService } from './idle-activity.service';
import { StorageHelperService } from './storage-helper.service';
import { GsUserService } from './user/gs-user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  // Emits true exactly once per fresh login (OAuth code-flow callback or loginWithPassword
  // success) — never on page reload with an already-valid token, silent refresh, or token
  // refresh. A BehaviorSubject replays its current value to new subscribers, so AnalyticsService
  // (constructed only after initAuth() has already resolved, during app bootstrap) still sees it.
  private freshLoginSubject$ = new BehaviorSubject<boolean>(false);
  public freshLogin$ = this.freshLoginSubject$.asObservable();

  private isLoggingOut = false;
  public guestMode = false;

  // User full name management
  private userFullNameSubject$ = new BehaviorSubject<string>('User');
  public userFullName$ = this.userFullNameSubject$.asObservable();

  // User profile picture management
  private userProfilePictureSubject$ = new BehaviorSubject<string | null>(null);
  public userProfilePicture$ = this.userProfilePictureSubject$.asObservable();

  // Detect iOS devices (iPhone, iPad, iPod)
  private isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  private isMacSafari = /Macintosh/.test(navigator.userAgent) &&
                        /Safari/.test(navigator.userAgent) &&
                        !/Chrome/.test(navigator.userAgent) &&
                        !/Chromium/.test(navigator.userAgent);

  private needsSafariHandling = this.isIOS || this.isMacSafari;

  private authConfig: AuthConfig = {
    issuer: environment.keycloak.issuer,
    redirectUri: window.location.origin,
    // Sent as the OIDC-spec `post_logout_redirect_uri` param on logOut(). Must NOT be passed as a
    // logOut() customParameter instead — that appends it as a non-standard `postLogoutRedirectUri`
    // query key carrying a raw absolute URL, which SSO edge/WAF rules can flag as an open-redirect
    // probe and block with a 403.
    postLogoutRedirectUri: window.location.origin + '/home',
    clientId: environment.keycloak.clientId,
    responseType: environment.keycloak.responseType,
    scope: environment.keycloak.scope,
    showDebugInformation: environment.keycloak.showDebugInformation,
    disableAtHashCheck: environment.keycloak.disableAtHashCheck,
    sessionChecksEnabled: this.needsSafariHandling ? false : environment.keycloak.sessionChecksEnabled,
    useSilentRefresh: this.needsSafariHandling ? false : environment.keycloak.useSilentRefresh,
    // Use dedicated silent refresh page to handle OAuth callback in iframe without affecting main page
    silentRefreshRedirectUri: window.location.origin + '/silent-refresh.html',
    // iOS-friendly settings:
    timeoutFactor: 0.75,
  };

  constructor(
    private oauthService: OAuthService,
    private router: Router,
    private storageHelper: StorageHelperService,
    private snackBar: MatSnackBar,
    private idleActivityService: IdleActivityService,
    private gsUserService: GsUserService
  ) {
    // Cross-tab logout detection: if another tab clears the access_token, log out here too
    window.addEventListener('storage', (event) => {
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }
      if (!this.oauthService.hasValidAccessToken()) {
        this.logout();
      }
    });
  }

  public configure(): void {
    // Skip OAuth configuration if auth is disabled
    if (!(environment as any).enableAuth) {
      return;
    }

    this.oauthService.setStorage(localStorage);
    this.oauthService.configure(this.authConfig);

    if (!this.needsSafariHandling) {
      this.oauthService.setupAutomaticSilentRefresh();
      this.idleActivityService.start();
    }

    // // Subscribe to all OAuth events for debugging
    //  this.oauthService.events.subscribe((event) => {
    //    console.log('OAuth Event:', event);
    //  });

    this.oauthService.events
      .pipe(filter((e) => e.type === 'token_received'))
      .subscribe(() => {
        this.isAuthenticatedSubject$.next(true);
      });

    // Keycloak says the session is over — verify before logging out.
    // session_terminated = session check iframe detected the SSO session ended
    // session_error = session check iframe failed to reach Keycloak
    //
    // In private/incognito windows, third-party cookies are blocked so the session check
    // iframe cannot send Keycloak's session cookie. Keycloak then reports the session as
    // gone even though the user is still authenticated (the access/refresh tokens are valid).
    // To avoid false-positive logouts, we attempt a token refresh first. If the refresh
    // succeeds the user is still authenticated and we stay logged in. Only if refresh also
    // fails do we treat the session as truly expired.
    this.oauthService.events
      .pipe(filter((e) => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(async (event) => {
        console.warn('[AuthService] Keycloak session check event:', event.type);
        if (!this.isAuthenticatedSubject$.value) return;

        try {
          await this.refreshToken();
          console.log('[AuthService] Session check failed but token refresh succeeded — session still active (likely private/incognito window)');
          // Session is still valid (e.g. private window blocking cookies), do not log out
        } catch {
          console.warn('[AuthService] Session ended and token refresh failed — logging out:', event.type);
          this.sessionExpiredLogout();
        }
      });

    // Explicit logout event
    this.oauthService.events
      .pipe(filter((e) => e.type === 'logout'))
      .subscribe(() => {
        this.handleSessionLost();
      });

    // Silent refresh failed — the Keycloak session may be expired, log out.
    //
    // In private/incognito windows the silent refresh iframe is also blocked by cookie
    // restrictions. Fall back to a direct token refresh (no iframe) to confirm before
    // logging out. If that succeeds the user is still authenticated.
    // Note: token_refresh_error means the direct refresh already failed, so log out immediately.
    this.oauthService.events
      .pipe(filter((e) => ['silent_refresh_error', 'silent_refresh_timeout', 'token_refresh_error'].includes(e.type)))
      .subscribe(async (event) => {
        if (!this.isAuthenticatedSubject$.value) return;
        console.warn('[AuthService] Token refresh event:', event.type);

        if (event.type === 'token_refresh_error') {
          // Direct refresh already failed — session is truly expired
          this.sessionExpiredLogout();
          return;
        }

        // For silent_refresh_error / silent_refresh_timeout (iframe-based), try a direct
        // token refresh before deciding to log out (covers private/incognito windows).
        try {
          await this.refreshToken();
          console.log('[AuthService] Silent refresh iframe failed but direct token refresh succeeded — session still active');
        } catch {
          console.warn('[AuthService] Silent refresh and fallback token refresh both failed — logging out');
          this.sessionExpiredLogout();
        }
      });

    if (this.needsSafariHandling) {
      this.oauthService.events
        .pipe(filter((e) => e.type === 'token_expires'))
        .subscribe(() => {
          this.refreshToken().catch(err => {
            console.error('Failed to refresh token on iOS:', err);
            // If refresh fails, session has expired — notify and log out
            this.sessionExpiredLogout();
          });
        });
    }
  }

  private handleSessionLost(): void {
    this.isAuthenticatedSubject$.next(false);
    this.storageHelper.removeItem('gs_selected_fund_state');
  }

  public async initAuth(): Promise<boolean> {
    // Skip authentication if disabled in environment
    if (!(environment as any).enableAuth) {
      this.isAuthenticatedSubject$.next(true);
      return true;
    }

    // New tab opened from openGivingLinkAsGuest — skip auth so header shows Login button
    if (window.location.search.includes('giveAsGuest=true')) {
      this.guestMode = true;
      await this.oauthService.loadDiscoveryDocument();
      this.isAuthenticatedSubject$.next(false);
      return false;
    }

    // A `code` param is only present when the browser was just redirected back from Keycloak's
    // code flow. loadDiscoveryDocumentAndTryLogin() consumes/strips it, so it must be read first —
    // this is what distinguishes a fresh login from a returning user whose token is still valid.
    const hadAuthCode = new URLSearchParams(window.location.search).has('code');

    try {
      // Load discovery document and try to login if there's an OAuth callback
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      if (this.oauthService.hasValidAccessToken()) {
        this.isAuthenticatedSubject$.next(true);
        if (hadAuthCode) {
          this.freshLoginSubject$.next(true);
        }
        return true;
      }

      // No valid access token — check if Keycloak still has an active session via silent refresh.
      // This enables SSO: if the user logged in from another domain using the same Keycloak realm,
      // they'll be automatically logged in here without seeing the login page.
      // Skip on iOS (Safari blocks third-party cookies needed for iframe) and for first-time visitors.
      const hasRefreshToken = !!this.oauthService.getRefreshToken();

      if (!this.needsSafariHandling && hasRefreshToken) {
        try {
          await this.oauthService.silentRefresh();

          if (this.oauthService.hasValidAccessToken()) {
            console.log('[AuthService] Session recovered via silent refresh');
            this.isAuthenticatedSubject$.next(true);
            return true;
          }
        } catch (silentRefreshError: any) {
          const interactionErrors = [
            'interaction_required', 'login_required',
            'account_selection_required', 'consent_required'
          ];
          if (silentRefreshError?.reason?.error &&
              interactionErrors.includes(silentRefreshError.reason.error)) {
            console.log('[AuthService] Silent refresh requires interaction, redirecting to login');
            this.login();
            return false;
          }
          // Other failures are expected when user has no active session
        }
      }

      this.isAuthenticatedSubject$.next(false);
      return false;
    } catch (error) {
      console.error('Error during authentication initialization:', error);
      this.isAuthenticatedSubject$.next(false);
      return false;
    }
  }

  public login(targetUrl?: string): void {
    // Skip OAuth login if auth is disabled
    if (!(environment as any).enableAuth) {
      this.isAuthenticatedSubject$.next(true);
      if (targetUrl) {
        this.router.navigate([targetUrl]);
      }
      return;
    }

    // Guest tab: tokens are already in localStorage from the original tab.
    // Reload the app root without giveAsGuest=true so initAuth() runs normally.
    if (this.guestMode) {
      window.location.href = window.location.origin;
      return;
    }

    // Trigger Keycloak OAuth flow
    try {
      if (targetUrl) {
        this.oauthService.initCodeFlow(targetUrl);
      } else {
        this.oauthService.initCodeFlow();
      }
    } catch (error) {
      console.error('Error initiating OAuth flow:', error);
    }
  }

  /**
   * Redirects to Keycloak's forgot password page
   * @param returnUrl - Optional URL to redirect back to after password reset
   */
  public forgotPassword(returnUrl?: string): void {
    // Skip if auth is disabled
    if (!(environment as any).enableAuth) {
      return;
    }

    // Construct Keycloak forgot password URL
    const redirectUri = returnUrl || window.location.href;
    const issuer = environment.keycloak.issuer;
    const clientId = environment.keycloak.clientId;

    // Build the forgot password URL with kc_action=UPDATE_PASSWORD
    const forgotPasswordUrl = `${issuer}/protocol/openid-connect/auth?` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=openid` +
      `&kc_action=UPDATE_PASSWORD`;

    // Redirect to Keycloak forgot password page
    window.location.href = forgotPasswordUrl;
  }

  /**
   * Gracefully logs out the user when their session has expired.
   * Guards against multiple concurrent calls (e.g. from simultaneous 401 responses)
   * and notifies the user before redirecting.
   */
  public sessionExpiredLogout(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    this.snackBar.open('Your session has expired. You have been logged out.', '', {
      duration: 4000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['toast-info'],
    });

    this.logout();
  }

  public logout(): void {
    this.idleActivityService.stop();

    // Invalidate cached user info so the next login fetches fresh data.
    this.gsUserService.resetUserInfoCache();

    // Clear session storage on logout
    this.storageHelper.removeItem('gs_selected_fund_state');
    // Also clear any pending redirect from custom login
    sessionStorage.removeItem('gs_pending_redirect');
    // Clear stored currency so the next login resolves fresh from the user's globalCurrency
    localStorage.removeItem('selectedCurrency');
    localStorage.removeItem('selectedCurrencyUserChosen');

    // Skip OAuth logout if auth is disabled
    if (!(environment as any).enableAuth) {
      this.isAuthenticatedSubject$.next(false);
      this.router.navigate(['/home']);
      return;
    }

    // Update authentication state
    this.isAuthenticatedSubject$.next(false);

    // Check if we have an id_token for proper Keycloak logout
    const idToken = this.oauthService.getIdToken();

    if (idToken) {
      // We have an id_token, perform proper Keycloak logout.
      // postLogoutRedirectUri is set on authConfig above, so the library appends it as the
      // spec-correct `post_logout_redirect_uri` query param — do not pass it as a customParameter
      // here (see authConfig comment).
      this.oauthService.logOut();
    } else {
      // No id_token available (e.g., from password flow without openid scope)
      // Clear tokens manually from localStorage without contacting Keycloak

      // Manually clear all OAuth-related tokens from localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('nonce');
      localStorage.removeItem('PKCE_verifier');
      localStorage.removeItem('expires_at');
      localStorage.removeItem('id_token_claims_obj');
      localStorage.removeItem('id_token_expires_at');
      localStorage.removeItem('id_token_stored_at');
      localStorage.removeItem('access_token_stored_at');
      localStorage.removeItem('granted_scopes');
      localStorage.removeItem('session_state');

      // Navigate to home page
      this.router.navigate(['/home']);
    }
  }

  public hasValidToken(): boolean {
    // Always return true if auth is disabled
    if (!(environment as any).enableAuth) {
      return true;
    }
    return this.oauthService.hasValidAccessToken();
  }

  public hasRefreshToken(): boolean {
    return !!this.oauthService.getRefreshToken();
  }

  public getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  public getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  public getIdentityClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  /**
   * Gets user claims from either id_token or access_token
   * Falls back to access_token if id_token is not available (e.g., password flow)
   */
  private getUserClaims(): any {
    // First try to get claims from id_token
    let claims = this.getIdentityClaims();

    // If no id_token claims, try to decode access_token
    if (!claims) {
      const accessToken = this.getAccessToken();
      if (accessToken) {
        try {
          const tokenParts = accessToken.split('.');
          if (tokenParts.length === 3) {
            const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            );
            claims = JSON.parse(jsonPayload);
          }
        } catch (error) {
          console.error('[AuthService] Error decoding access token for user claims:', error);
          return null;
        }
      }
    }

    return claims;
  }

  public get userName(): string | null {
    const claims = this.getUserClaims();
    return claims ? claims['preferred_username'] || claims['name'] || null : null;
  }

  public get userEmail(): string | null {
    const claims = this.getUserClaims();
    return claims ? claims['email'] || null : null;
  }

  public get userProfilePicture(): string | null {
    return this.userProfilePictureSubject$.value;
  }

  public get userFullName(): string | null {
    const claims = this.getUserClaims();
    return claims ? claims['name'] || claims['preferred_username'] || null : null;
  }

  /**
   * Initialize user name from token
   * Called when user logs in or page loads
   */
  public initializeUserNameFromToken(fullName: string | null): void {
    if (fullName) {
      this.userFullNameSubject$.next(fullName);
    }
  }

  /**
   * Update user name after profile save
   * This method is called when user updates their name in settings
   * @param firstName - User's first name
   * @param lastName - User's last name
   */
  public updateUserFullName(firstName: string, lastName: string): void {
    const fullName = `${firstName} ${lastName}`.trim();
    this.userFullNameSubject$.next(fullName);
  }

  /**
   * Get current user full name value
   */
  public getCurrentUserFullName(): string {
    return this.userFullNameSubject$.value;
  }

  /**
   * Initialize user profile picture from token
   * Called when user logs in or page loads
   */
  public initializeUserProfilePictureFromToken(): void {
    const claims = this.getUserClaims();
    const profilePicture = claims ? claims['profile_picture'] || claims['picture'] || null : null;
    if (profilePicture) {
      this.userProfilePictureSubject$.next(profilePicture);
    }
  }

  /**
   * Update user profile picture after upload
   * This method is called when user uploads a new profile picture
   * @param profilePictureUrl - URL of the new profile picture
   */
  public updateUserProfilePicture(profilePictureUrl: string | null): void {
    this.userProfilePictureSubject$.next(profilePictureUrl);
  }

  /**
   * Get current user profile picture value
   */
  public getCurrentUserProfilePicture(): string | null {
    return this.userProfilePictureSubject$.value;
  }

  public get userUuid(): string | null {
    const claims = this.getUserClaims();
    return claims ? claims['gs_uuid'] || claims['sub'] || null : null;
  }

  public get userRolePrivileges(): Map<string, string[]> | null {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      console.warn('[AuthService] No access token available for role extraction');
      return null;
    }

    try {
      // Decode JWT token (format: header.payload.signature)
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        console.error('[AuthService] Invalid JWT token format - expected 3 parts, got:', tokenParts.length);
        return null;
      }

      // Decode the payload (second part)
      // The payload is base64url encoded (not standard base64)
      let payload;
      try {
        const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        payload = JSON.parse(jsonPayload);
      } catch (decodeError) {
        console.error('[AuthService] Error decoding token payload:', decodeError);
        console.error('[AuthService] Token payload (first 50 chars):', tokenParts[1].substring(0, 50));
        return null;
      }

      // Extract roles from different possible locations in the token
      let roles: string[] = [];

      // Check for realm_access.roles (Keycloak realm roles)
      if (payload.realm_access && Array.isArray(payload.realm_access.roles)) {
        roles = [...roles, ...payload.realm_access.roles];
      }

      // Check for resource_access roles (Keycloak client roles)
      if (payload.resource_access) {
        Object.keys(payload.resource_access).forEach(client => {
          if (Array.isArray(payload.resource_access[client].roles)) {
            roles = [...roles, ...payload.resource_access[client].roles];
          }
        });
      }

      // Check for direct roles array
      if (Array.isArray(payload.roles)) {
        roles = [...roles, ...payload.roles];
      }

      // Remove duplicates
      roles = [...new Set(roles)];

      const result = new Map<string, string[]>();
      // Look for primary GS_ role, fall back to PORTAL_ role
      let key = roles.find(item => item.startsWith("GS_")) || null;

      if (!key) {
          key = roles.find(item => item.startsWith("PORTAL_")) || null;
          if (!key) {
              return null; // No GS_ or PORTAL_ role found
          }
      }

      const values = roles.filter(
          role => !role.startsWith("GS_") && !role.startsWith("PORTAL_")
      );

      result.set(key, values);
      return result.size > 0 ? result : null;
    } catch (error) {
      console.error('[AuthService] Error decoding access token:', error);
      console.error('[AuthService] This may indicate the token format is incompatible');
      return null;
    }
  }

  /**
   * Determines the app type based on user's roles for translation filtering
   * @returns "GS" if user has only GS_* roles, "PORTAL" if only PORTAL_* roles, null if both or none
   */
  public getAppType(): 'GS' | 'PORTAL' | null {
    const roles = this.getAllRoles();
    if (!roles) return null;

    const hasGsRoles = roles.some(role => role.startsWith('GS_'));
    const hasPortalRoles = roles.some(role => role.startsWith('PORTAL'));

    if (hasGsRoles && hasPortalRoles) {
      return null; // User has both, don't filter
    } else if (hasGsRoles) {
      return 'GS';
    } else if (hasPortalRoles) {
      return 'PORTAL';
    }

    return null;
  }

  /**
   * Checks if the user is a Portal Admin only user (PORTAL_ADMIN without any GS_ roles)
   * These users have no funds and should be redirected to settings/fund-settings
   */
  public isPortalAdminOnly(): boolean {
    const roles = this.getAllRoles();
    if (!roles) return false;

    const hasPortalAdmin = roles.includes('PORTAL_ADMIN');
    const hasGsRoles = roles.some(role => role.startsWith('GS_'));
    const hasPortalUser = roles.includes('PORTAL_USER');

    return hasPortalAdmin && !hasGsRoles && !hasPortalUser;
  }

  /**
   * Checks if the user has portal access (PORTAL_ADMIN or PORTAL_USER)
   */
  public hasPortalAccess(): boolean {
    const roles = this.getAllRoles();
    if (!roles) return false;
    return roles.some(role => role.startsWith('PORTAL'));
  }

  /**
   * Checks if the user has GivingSpace access (any GS_* role)
   * Used to gate features that should not be available to portal-only users
   */
  public hasGivingSpaceAccess(): boolean {
    const roles = this.getAllRoles();
    if (!roles) return false;
    return roles.some(role => role.startsWith('GS_'));
  }

  /**
   * Determines the correct landing page based on user's access type
   * Portal access (including Portal Admin) -> Fund Dashboard
   * GS only -> Home page
   * Note: Portal Admin without funds will be redirected to settings after funds fail to load
   */
  public getLandingRoute(): string {
    if (this.hasPortalAccess()) {
      return '/fund/dashboard';
    }
    return '/home';
  }

  /**
   * Extracts all roles from the access token
   * @returns Array of role strings, or null if no token available
   */
  private getAllRoles(): string[] | null {
    const accessToken = this.getAccessToken();
    if (!accessToken) return null;

    try {
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) return null;

      const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);

      let roles: string[] = [];

      if (payload.realm_access?.roles) {
        roles = [...roles, ...payload.realm_access.roles];
      }

      if (payload.resource_access) {
        Object.keys(payload.resource_access).forEach(client => {
          if (payload.resource_access[client].roles) {
            roles = [...roles, ...payload.resource_access[client].roles];
          }
        });
      }

      if (Array.isArray(payload.roles)) {
        roles = [...roles, ...payload.roles];
      }

      return [...new Set(roles)];
    } catch (error) {
      console.error('[AuthService] Error extracting roles:', error);
      return null;
    }
  }

  /**
   * Checks if the user has a specific role
   * Searches across realm roles, resource access roles, and direct roles array
   * @param roleName - The name of the role to check for (e.g., 'CHAMPION_ADMIN', 'PORTAL_USER')
   * @returns true if user has the specified role, false otherwise
   */
  public hasRole(roleName: string): boolean {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return false;
    }

    try {
      // Decode JWT token
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }

      // Decode the payload
      const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);

      // Collect all roles from different locations
      let allRoles: string[] = [];

      // Check for realm_access.roles (Keycloak realm roles)
      if (payload.realm_access && Array.isArray(payload.realm_access.roles)) {
        allRoles = [...allRoles, ...payload.realm_access.roles];
      }

      // Check for resource_access roles (Keycloak client roles)
      if (payload.resource_access) {
        Object.keys(payload.resource_access).forEach(client => {
          if (Array.isArray(payload.resource_access[client].roles)) {
            allRoles = [...allRoles, ...payload.resource_access[client].roles];
          }
        });
      }

      // Check for direct roles array
      if (Array.isArray(payload.roles)) {
        allRoles = [...allRoles, ...payload.roles];
      }

      // Check if the specified role exists
      return allRoles.includes(roleName);
    } catch (error) {
      console.error(`[AuthService] Error checking role '${roleName}':`, error);
      return false;
    }
  }

  /**
   * Checks if the user has the PORTAL_USER role in the backend client
   * This is used to determine if the user can access donor/portal features
   * @returns true if user has PORTAL_USER role in backendClientId, false otherwise
   */
  public hasPortalUserRole(): boolean {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return false;
    }

    try {
      // Decode JWT token
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }

      // Decode the payload
      const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);

      // Get backend client ID from environment
      const backendClientId = environment.fund.backendClientId;

      // Check if user has PORTAL_USER role in the backend client
      if (payload.resource_access?.[backendClientId]?.roles) {
        const roles: string[] = payload.resource_access[backendClientId].roles;
        return roles.includes('PORTAL_USER');
      }

      return false;
    } catch (error) {
      console.error('[AuthService] Error checking PORTAL_USER role:', error);
      return false;
    }
  }

  /**
   * Manually updates the authentication state
   * Used when authentication state needs to be updated independently of login/logout
   * @param isAuthenticated - The authentication state to set
   */
  public updateAuthenticationState(isAuthenticated: boolean): void {
    this.isAuthenticatedSubject$.next(isAuthenticated);
  }

  /**
   * Consumed by AnalyticsService right after it handles a fresh-login event, so that a
   * subsequent reload (which leaves freshLogin$ at its last value otherwise) doesn't re-fire it.
   */
  public clearFreshLoginFlag(): void {
    this.freshLoginSubject$.next(false);
  }

  /**
   * Clears all OAuth tokens from localStorage without navigation
   * Used when we need to keep user on current page but ensure they're logged out
   * This is particularly useful for donor-login flow when user doesn't have required role
   */
  public clearTokensWithoutNavigation(): void {
    // Invalidate cached user info so the next user fetches fresh data.
    this.gsUserService.resetUserInfoCache();

    // Manually clear all OAuth-related tokens from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('nonce');
    localStorage.removeItem('PKCE_verifier');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('id_token_claims_obj');
    localStorage.removeItem('id_token_expires_at');
    localStorage.removeItem('id_token_stored_at');
    localStorage.removeItem('access_token_stored_at');
    localStorage.removeItem('granted_scopes');
    localStorage.removeItem('session_state');
    sessionStorage.removeItem('gs_pending_redirect');

    // Update authentication state to false
    this.isAuthenticatedSubject$.next(false);
  }

  public refreshToken(): Promise<any> {
    return this.oauthService.refreshToken();
  }

  /**
   * Refresh token and reinitialize user data from the new token
   * Call this after updating profile picture or user name to ensure token has latest values
   */
  public async refreshTokenAndUserData(): Promise<void> {
    try {
      await this.refreshToken();

      // Reinitialize user data from the refreshed token
      this.initializeUserNameFromToken(this.userFullName);
      this.initializeUserProfilePictureFromToken();

      return Promise.resolve();
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return Promise.reject(error);
    }
  }

  /**
   * Login with username and password using Resource Owner Password Credentials grant
   * This method provides the same authentication experience as the regular Keycloak OAuth flow:
   * - Obtains and stores access tokens in localStorage
   * - Updates authentication state across the application
   * - Enables all authenticated features (profile picture, user menu, etc.)
   * - Maintains session persistence (user stays logged in across page refreshes)
   * - Requests openid scope to get id_token for proper logout
   *
   * @param username - User's username or email
   * @param password - User's password
   * @param skipAuthStateUpdate - If true, does not update authentication state (caller must do it manually)
   * @returns Promise that resolves with authentication result
   */
  public async loginWithPassword(username: string, password: string, skipAuthStateUpdate: boolean = false): Promise<{ success: boolean; error?: string }> {
    // Skip OAuth login if auth is disabled
    if (!(environment as any).enableAuth) {
      if (!skipAuthStateUpdate) {
        this.isAuthenticatedSubject$.next(true);
      }
      return { success: true };
    }

    try {
      // Clear any existing nonce to prevent "Wrong nonce" errors in password flow
      // Password flow doesn't use nonce (that's for authorization code flow)
      localStorage.removeItem('nonce');

      // Use OAuth library's password flow (Resource Owner Password Credentials Grant)
      // IMPORTANT: The scope is configured in authConfig and should include 'openid' to get id_token
      // The OAuth library will automatically use the scope from authConfig
      // This stores tokens in localStorage just like the regular OAuth flow
      const result = await this.oauthService.fetchTokenUsingPasswordFlow(username, password);

      if (result && this.oauthService.hasValidAccessToken()) {
        // Debug: Verify tokens are written to localStorage and log user info
        const accessToken = localStorage.getItem('access_token');
        const idToken = localStorage.getItem('id_token');
        console.log('[AuthService] loginWithPassword successful. Tokens stored:', {
          hasAccessToken: !!accessToken,
          hasIdToken: !!idToken,
          userName: this.userName,
          userEmail: this.userEmail,
          userFullName: this.userFullName
        });

        // Update authentication state - this triggers all subscribed components to update
        // Only update if skipAuthStateUpdate is false (default behavior)
        if (!skipAuthStateUpdate) {
          this.isAuthenticatedSubject$.next(true);
        }

        // The authentication state will propagate to all components via isAuthenticated$ observable
        // Components like Header will update to show profile picture and user menu
        // The user will remain logged in even after page refresh or navigation

        // Password-flow login is always a fresh login (never a reload/refresh) — fire regardless
        // of skipAuthStateUpdate, since the credential exchange itself succeeded.
        this.freshLoginSubject$.next(true);

        return { success: true };
      } else {
        return { success: false, error: 'Invalid username or password' };
      }
    } catch (error: any) {
      console.error('Password login error:', error);

      // Parse error message from OAuth server
      let errorMessage = 'Login failed. Please check your credentials.';

      if (error?.error === 'invalid_grant') {
        errorMessage = 'Invalid username or password';
      } else if (error?.error === 'unauthorized_client') {
        errorMessage = 'Login method not supported';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }
}
